import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssignQPToSourceSiteCommand,
  AssignUserToSourceSiteCommand,
  BatchQueryResultViewModel,
  CompaniesClient,
  CreateSourceSiteCommand,
  EditSourceSiteCommand,
  ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse,
  ISourceSiteViewModel,
  SourceSiteAttachmentViewModel,
  SourceSiteCleanFillUsersClient,
  SourceSiteQPSearchViewModelTotalTypePagedResultResponse,
  SourceSiteQualifiedPersonsClient,
  SourceSitesClient,
  SourceSiteSearchQuery,
  SourceSiteUserSearchViewModelTotalTypePagedResultResponse
} from '@services/cleanfill-api/cleanfill-api.generated';
import { AuthService } from '@services/core/auth.service';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { Observable } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HttpSourceSitesService {
  constructor(
    private srcSiteClient: SourceSitesClient,
    private srcSiteUsersClient: SourceSiteCleanFillUsersClient,
    private srcSiteQpClient: SourceSiteQualifiedPersonsClient,
    private companiesClient: CompaniesClient,
    private sourceSitesClient: SourceSitesClient,
    private authService: AuthService,
    private http: HttpClient
  ) { }

  getSourceSitesById(uniqueId: string, query: ISearchQuery): Observable<ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    const sourceQuery = new SourceSiteSearchQuery({
      filterBy: query.FilterBy,
      sortBy: query.SortBy.length ? query.SortBy : undefined,
      page: query.Page,
      pageSize: query.PageSize,
    });
    return this.companiesClient.searchSourceSite(uniqueId, undefined, sourceQuery);
  }
  getSourceSites(query: ISearchQuery): Observable<ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    const sourceQuery = new SourceSiteSearchQuery({
      filterBy: query.FilterBy,
      sortBy: query.SortBy.length ? query.SortBy : undefined,
      page: query.Page,
      pageSize: query.PageSize,
    });
    return this.authService.activeUserLegalEntityUniqueId$.pipe(
      concatMap((uniqueId) => {
        return this.companiesClient.searchSourceSite(uniqueId, undefined, sourceQuery);
      })
    );
  }
  getALLSourceSites(query: ISearchQuery): Observable<ISourceSiteSearchViewModelSiteTotalTypePagedResultResponse> {
    return this.sourceSitesClient.searchSourceSites(query.Page, query.PageSize, query.SortBy, query.FilterBy, undefined);
  }

  getSourceSiteQps(srcSiteUniqueId: string) {
    return this.srcSiteClient.getSourceSiteQualifiedPersons(srcSiteUniqueId, new Date().getTimezoneOffset().toString());
  }

  getBatches(sourceSiteUniqueId: string): Observable<BatchQueryResultViewModel[]> {
    return this.sourceSitesClient.sourceSiteBatches(sourceSiteUniqueId, new Date().getTimezoneOffset().toString());
  }

  createSourceSite(payload: CreateSourceSiteCommand): Observable<ISourceSiteViewModel> {
    return this.srcSiteClient.createSourceSite(undefined, payload);
  }

  editSourceSite(payload: EditSourceSiteCommand) {
    return this.srcSiteClient.editSourceSite(payload.uniqueId, undefined, payload);
  }

  uploadAttachments(data: FormData): Observable<number> {
    return this.http.post<number>('/api/SourceSites/Attachment', data);
  }

  getAttachments(uniqueId: string): Observable<SourceSiteAttachmentViewModel[]> {
    return this.srcSiteClient.getSourceSiteAttachmentDetail(uniqueId, undefined);
  }

  deleteAttachment(uniqueId: string): Observable<void> {
    return this.srcSiteClient.deleteSourceSiteAttachment(uniqueId, undefined).pipe(tap(() => window.stop()));
  }

  deleteSourceSite(uniqueId: string): Observable<void> {
    return this.srcSiteClient.deleteSourceSite(uniqueId, undefined);
  }

  archiveSourceSite(uniqueId: string): Observable<void> {
    return this.srcSiteClient.archiveSourceSite(uniqueId, undefined);
  }

  reactivateSourceSite(uniqueId: string): Observable<void> {
    return this.srcSiteClient.reactivateSourceSite(uniqueId, undefined);
  }

  getSourceSite(uniqueId: string): Observable<ISourceSiteViewModel> {
    return this.srcSiteClient.getSourceSiteDetail(uniqueId, undefined);
  }

  getSourceSiteUsers(uniqueId: string, query: ISearchQuery): Observable<SourceSiteUserSearchViewModelTotalTypePagedResultResponse> {
    return this.srcSiteClient.getSourceSiteAssignedUsers(
      uniqueId,
      uniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      undefined
    );
  }

  getSourceSiteAssignedQps(uniqueId: string, query: ISearchQuery): Observable<SourceSiteQPSearchViewModelTotalTypePagedResultResponse> {
    return this.srcSiteClient.getSourceSiteAssignedQPs(
      uniqueId,
      uniqueId,
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      undefined
    );
  }

  unassignUser(cleanFillUserUniqueId: string) {
    return this.srcSiteUsersClient.unassignUserFromSourceSite(cleanFillUserUniqueId, undefined);
  }

  assignUser(sourceSiteUniqueId: string, cleanFillUserUniqueId: string) {
    return this.srcSiteUsersClient.assignUserToSourceSite(
      undefined,
      new AssignUserToSourceSiteCommand({ sourceSiteUniqueId, cleanFillUserUniqueId })
    );
  }

  unassignQp(qpUniqueId: string): Observable<void> {
    return this.srcSiteQpClient.unassignQPFromSourceSite(qpUniqueId, undefined);
  }

  assignQp(sourceSiteUniqueId: string, qualifiedPersonUniqueId: string): Observable<void> {
    return this.srcSiteQpClient.assignQPToSourceSite(
      undefined,
      new AssignQPToSourceSiteCommand({ sourceSiteUniqueId, qualifiedPersonUniqueId })
    );
  }
}
